export interface DrillCell {
  drill: string;
  decimal: string;
}

export const EMPTY_DRILL: DrillCell = { drill: '', decimal: '' };

export interface StandardTapRow {
  screwSize: string;
  majorDiameter: string;
  tpi: number | string;
  minorDiameter: string;
  tap75: DrillCell;
  tap50: DrillCell;
  closeFit: DrillCell;
  freeFit: DrillCell;
}

export interface MetricTapRow {
  screwSize: string;
  pitch: string;
  tap75: DrillCell;
  tap50: DrillCell;
  closeFit: DrillCell;
  freeFit: DrillCell;
}

function d(drill: string, decimal: string): DrillCell {
  return { drill, decimal };
}

function hasDrill(cell: DrillCell): boolean {
  return Boolean(cell.drill || cell.decimal);
}

function splitScrewGroups<T extends { screwSize: string }>(rows: T[]): T[][] {
  const groups: T[][] = [];
  let current: T[] = [];
  for (const row of rows) {
    if (row.screwSize) {
      if (current.length) groups.push(current);
      current = [row];
    } else {
      current.push(row);
    }
  }
  if (current.length) groups.push(current);
  return groups;
}

function isClearanceOnlyStandard(row: StandardTapRow): boolean {
  return (
    row.tpi === '' &&
    !row.minorDiameter &&
    !hasDrill(row.tap75) &&
    !hasDrill(row.tap50) &&
    (hasDrill(row.closeFit) || hasDrill(row.freeFit))
  );
}

function isClearanceOnlyMetric(row: MetricTapRow): boolean {
  return (
    !row.pitch &&
    !hasDrill(row.tap75) &&
    !hasDrill(row.tap50) &&
    (hasDrill(row.closeFit) || hasDrill(row.freeFit))
  );
}

/** Drop clearance-only leader rows; attach clearance to the first tap/thread row in each group. */
function compactStandardRows(rows: StandardTapRow[]): StandardTapRow[] {
  const out: StandardTapRow[] = [];
  for (const group of splitScrewGroups(rows)) {
    const clearanceOnly = group.filter(isClearanceOnlyStandard);
    const substantive = group.filter((r) => !isClearanceOnlyStandard(r));
    if (!substantive.length) {
      out.push(...group);
      continue;
    }
    const merged = substantive.map((row) => ({ ...row }));
    const first = merged[0];
    const leader = clearanceOnly[0];
    if (leader) {
      if (!first.screwSize && leader.screwSize) first.screwSize = leader.screwSize;
      if (!first.majorDiameter && leader.majorDiameter) first.majorDiameter = leader.majorDiameter;
      for (const co of clearanceOnly) {
        if (hasDrill(co.closeFit)) first.closeFit = { ...co.closeFit };
        if (hasDrill(co.freeFit)) first.freeFit = { ...co.freeFit };
      }
    }
    out.push(...merged);
  }
  return out;
}

function compactMetricRows(rows: MetricTapRow[]): MetricTapRow[] {
  const out: MetricTapRow[] = [];
  for (const group of splitScrewGroups(rows)) {
    const clearanceOnly = group.filter(isClearanceOnlyMetric);
    const substantive = group.filter((r) => !isClearanceOnlyMetric(r));
    if (!substantive.length) {
      out.push(...group);
      continue;
    }
    const merged = substantive.map((row) => ({ ...row }));
    const first = merged[0];
    const leader = clearanceOnly[0];
    if (leader) {
      if (!first.screwSize && leader.screwSize) first.screwSize = leader.screwSize;
      for (const co of clearanceOnly) {
        if (hasDrill(co.closeFit)) first.closeFit = { ...co.closeFit };
        if (hasDrill(co.freeFit)) first.freeFit = { ...co.freeFit };
      }
    }
    out.push(...merged);
  }
  return out;
}

/** UF EML2322L Tap & Drill Chart — Standard (imperial) section */
const STANDARD_TAP_ROWS_SOURCE: StandardTapRow[] = [
  {
    screwSize: '0',
    majorDiameter: '0.060',
    tpi: 80,
    minorDiameter: '0.045',
    tap75: d('3/64', '0.047'),
    tap50: d('55', '0.052'),
    closeFit: d('52', '0.064'),
    freeFit: d('50', '0.070'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 64,
    minorDiameter: '0.054',
    tap75: d('53', '0.060'),
    tap50: d('1/16', '0.063'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '1',
    majorDiameter: '0.073',
    tpi: '',
    minorDiameter: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: d('48', '0.076'),
    freeFit: d('46', '0.081'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 72,
    minorDiameter: '0.056',
    tap75: d('53', '0.060'),
    tap50: d('52', '0.064'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 56,
    minorDiameter: '0.064',
    tap75: d('50', '0.070'),
    tap50: d('49', '0.073'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '2',
    majorDiameter: '0.086',
    tpi: '',
    minorDiameter: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: d('43', '0.089'),
    freeFit: d('41', '0.096'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 64,
    minorDiameter: '0.067',
    tap75: d('50', '0.070'),
    tap50: d('48', '0.076'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 48,
    minorDiameter: '0.073',
    tap75: d('47', '0.079'),
    tap50: d('44', '0.086'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '3',
    majorDiameter: '0.099',
    tpi: '',
    minorDiameter: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: d('37', '0.104'),
    freeFit: d('35', '0.110'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 56,
    minorDiameter: '0.077',
    tap75: d('45', '0.082'),
    tap50: d('43', '0.089'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 40,
    minorDiameter: '0.081',
    tap75: d('43', '0.089'),
    tap50: d('41', '0.096'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '4',
    majorDiameter: '0.112',
    tpi: '',
    minorDiameter: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: d('32', '0.116'),
    freeFit: d('30', '0.129'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 48,
    minorDiameter: '0.086',
    tap75: d('42', '0.094'),
    tap50: d('40', '0.098'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 40,
    minorDiameter: '0.094',
    tap75: d('38', '0.102'),
    tap50: d('7/64', '0.109'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '5',
    majorDiameter: '0.125',
    tpi: '',
    minorDiameter: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: d('30', '0.129'),
    freeFit: d('29', '0.136'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 44,
    minorDiameter: '0.097',
    tap75: d('37', '0.104'),
    tap50: d('35', '0.110'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 32,
    minorDiameter: '0.100',
    tap75: d('36', '0.107'),
    tap50: d('32', '0.116'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '6',
    majorDiameter: '0.138',
    tpi: '',
    minorDiameter: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: d('27', '0.144'),
    freeFit: d('25', '0.150'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 40,
    minorDiameter: '0.107',
    tap75: d('33', '0.113'),
    tap50: d('31', '0.120'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 32,
    minorDiameter: '0.126',
    tap75: d('29', '0.136'),
    tap50: d('27', '0.144'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '8',
    majorDiameter: '0.164',
    tpi: '',
    minorDiameter: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: d('18', '0.170'),
    freeFit: d('16', '0.177'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 36,
    minorDiameter: '0.130',
    tap75: d('29', '0.136'),
    tap50: d('26', '0.147'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 24,
    minorDiameter: '0.139',
    tap75: d('22', '0.157'),
    tap50: d('20', '0.161'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '10',
    majorDiameter: '0.190',
    tpi: '',
    minorDiameter: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: d('9', '0.196'),
    freeFit: d('7', '0.201'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 32,
    minorDiameter: '0.152',
    tap75: d('21', '0.159'),
    tap50: d('18', '0.170'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 24,
    minorDiameter: '0.165',
    tap75: d('16', '0.177'),
    tap50: d('12', '0.189'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '12',
    majorDiameter: '0.216',
    tpi: 28,
    minorDiameter: '0.172',
    tap75: d('14', '0.182'),
    tap50: d('10', '0.194'),
    closeFit: d('2', '0.221'),
    freeFit: d('1', '0.228'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 32,
    minorDiameter: '0.178',
    tap75: d('13', '0.185'),
    tap50: d('9', '0.196'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 20,
    minorDiameter: '0.189',
    tap75: d('7', '0.201'),
    tap50: d('7/32', '0.219'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '1/4',
    majorDiameter: '0.250',
    tpi: 28,
    minorDiameter: '0.206',
    tap75: d('3', '0.213'),
    tap50: d('1', '0.228'),
    closeFit: d('F', '0.257'),
    freeFit: d('H', '0.266'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 32,
    minorDiameter: '0.212',
    tap75: d('7/32', '0.219'),
    tap50: d('1', '0.228'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 18,
    minorDiameter: '0.244',
    tap75: d('F', '0.257'),
    tap50: d('J', '0.277'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '5/16',
    majorDiameter: '0.313',
    tpi: 24,
    minorDiameter: '0.261',
    tap75: d('I', '0.272'),
    tap50: d('9/32', '0.281'),
    closeFit: d('P', '0.323'),
    freeFit: d('Q', '0.332'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 32,
    minorDiameter: '0.274',
    tap75: d('9/32', '0.281'),
    tap50: d('L', '0.290'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 16,
    minorDiameter: '0.298',
    tap75: d('5/16', '0.313'),
    tap50: d('Q', '0.332'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '3/8',
    majorDiameter: '0.375',
    tpi: 24,
    minorDiameter: '0.324',
    tap75: d('Q', '0.332'),
    tap50: d('S', '0.348'),
    closeFit: d('W', '0.386'),
    freeFit: d('X', '0.397'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 32,
    minorDiameter: '0.337',
    tap75: d('11/32', '0.344'),
    tap50: d('T', '0.358'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 14,
    minorDiameter: '0.350',
    tap75: d('U', '0.368'),
    tap50: d('25/64', '0.391'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '7/16',
    majorDiameter: '0.438',
    tpi: 20,
    minorDiameter: '0.376',
    tap75: d('25/64', '0.391'),
    tap50: d('13/32', '0.406'),
    closeFit: d('29/64', '0.453'),
    freeFit: d('15/32', '0.469'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 28,
    minorDiameter: '0.394',
    tap75: d('Y', '0.404'),
    tap50: d('Z', '0.413'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 13,
    minorDiameter: '0.406',
    tap75: d('27/64', '0.422'),
    tap50: d('29/64', '0.453'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '1/2',
    majorDiameter: '0.500',
    tpi: 20,
    minorDiameter: '0.439',
    tap75: d('29/64', '0.453'),
    tap50: d('15/32', '0.469'),
    closeFit: d('33/64', '0.516'),
    freeFit: d('17/32', '0.531'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 28,
    minorDiameter: '0.456',
    tap75: d('15/32', '0.469'),
    tap50: d('15/32', '0.469'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 12,
    minorDiameter: '0.460',
    tap75: d('31/64', '0.484'),
    tap50: d('33/64', '0.516'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '9/16',
    majorDiameter: '0.563',
    tpi: 18,
    minorDiameter: '0.494',
    tap75: d('33/64', '0.516'),
    tap50: d('17/32', '0.531'),
    closeFit: d('37/64', '0.578'),
    freeFit: d('19/32', '0.594'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 24,
    minorDiameter: '0.511',
    tap75: d('33/64', '0.516'),
    tap50: d('17/32', '0.531'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 11,
    minorDiameter: '0.514',
    tap75: d('17/32', '0.531'),
    tap50: d('9/16', '0.563'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '5/8',
    majorDiameter: '0.625',
    tpi: 18,
    minorDiameter: '0.557',
    tap75: d('37/64', '0.578'),
    tap50: d('19/32', '0.594'),
    closeFit: d('41/64', '0.641'),
    freeFit: d('21/32', '0.656'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 24,
    minorDiameter: '0.574',
    tap75: d('37/64', '0.578'),
    tap50: d('19/32', '0.594'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '11/16',
    majorDiameter: '0.688',
    tpi: 24,
    minorDiameter: '0.636',
    tap75: d('41/64', '0.641'),
    tap50: d('21/32', '0.656'),
    closeFit: d('45/64', '0.703'),
    freeFit: d('23/32', '0.719'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 10,
    minorDiameter: '0.627',
    tap75: d('21/32', '0.656'),
    tap50: d('11/16', '0.688'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '3/4',
    majorDiameter: '0.750',
    tpi: 16,
    minorDiameter: '0.673',
    tap75: d('11/16', '0.688'),
    tap50: d('45/64', '0.703'),
    closeFit: d('49/64', '0.766'),
    freeFit: d('25/32', '0.781'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 20,
    minorDiameter: '0.689',
    tap75: d('45/64', '0.703'),
    tap50: d('23/32', '0.719'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '13/16',
    majorDiameter: '0.813',
    tpi: 20,
    minorDiameter: '0.751',
    tap75: d('49/64', '0.766'),
    tap50: d('25/32', '0.781'),
    closeFit: d('53/64', '0.828'),
    freeFit: d('27/32', '0.844'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 9,
    minorDiameter: '0.739',
    tap75: d('49/64', '0.766'),
    tap50: d('51/64', '0.797'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '7/8',
    majorDiameter: '0.875',
    tpi: 14,
    minorDiameter: '0.787',
    tap75: d('13/16', '0.813'),
    tap50: d('53/64', '0.828'),
    closeFit: d('57/64', '0.8906'),
    freeFit: d('29/32', '0.9062'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 20,
    minorDiameter: '0.814',
    tap75: d('53/64', '0.828'),
    tap50: d('27/32', '0.844'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '15/16',
    majorDiameter: '0.938',
    tpi: 20,
    minorDiameter: '0.876',
    tap75: d('57/64', '0.891'),
    tap50: d('29/32', '0.906'),
    closeFit: d('61/64', '0.953'),
    freeFit: d('31/32', '0.969'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 8,
    minorDiameter: '0.847',
    tap75: d('7/8', '0.875'),
    tap50: d('59/64', '0.922'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '1',
    majorDiameter: '1',
    tpi: 12,
    minorDiameter: '0.898',
    tap75: d('15/16', '0.938'),
    tap50: d('61/64', '0.953'),
    closeFit: d('1-1/64', '1.0156'),
    freeFit: d('1-1/32', '1.0313'),
  },
  {
    screwSize: '',
    majorDiameter: '',
    tpi: 20,
    minorDiameter: '0.939',
    tap75: d('61/64', '0.953'),
    tap50: d('31/32', '0.969'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
];

/** Metric drill cells use mm in `drill` and American size in `decimal` for tap columns; clearance uses mm + American */
function m(mm: string, american: string): DrillCell {
  return { drill: mm, decimal: american };
}

/** UF EML2322L Tap & Drill Chart — Metric section */
const METRIC_TAP_ROWS_SOURCE: MetricTapRow[] = [
  {
    screwSize: 'M1.5',
    pitch: '0.35',
    tap75: m('1.15', '56'),
    tap50: m('1.25', '55'),
    closeFit: m('1.60', '1/16'),
    freeFit: m('1.65', '52'),
  },
  {
    screwSize: 'M1.6',
    pitch: '0.35',
    tap75: m('1.25', '55'),
    tap50: m('1.35', '54'),
    closeFit: m('1.70', '51'),
    freeFit: m('1.75', '50'),
  },
  {
    screwSize: 'M1.8',
    pitch: '0.35',
    tap75: m('1.45', '53'),
    tap50: m('1.55', '1/16'),
    closeFit: m('1.90', '49'),
    freeFit: m('2.00', '5/64'),
  },
  {
    screwSize: '',
    pitch: '0.45',
    tap75: m('1.55', '1/16'),
    tap50: m('1.70', '51'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M2',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('2.10', '45'),
    freeFit: m('2.20', '44'),
  },
  {
    screwSize: '',
    pitch: '0.40',
    tap75: m('1.60', '52'),
    tap50: m('1.75', '50'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M2.2',
    pitch: '0.45',
    tap75: m('1.75', '50'),
    tap50: m('1.90', '48'),
    closeFit: m('2.30', '3/32'),
    freeFit: m('2.40', '41'),
  },
  {
    screwSize: 'M2.5',
    pitch: '0.45',
    tap75: m('2.05', '46'),
    tap50: m('2.20', '44'),
    closeFit: m('2.65', '37'),
    freeFit: m('2.75', '7/64'),
  },
  {
    screwSize: '',
    pitch: '0.60',
    tap75: m('2.40', '41'),
    tap50: m('2.60', '37'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M3',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('3.15', '1/8'),
    freeFit: m('3.30', '30'),
  },
  {
    screwSize: '',
    pitch: '0.50',
    tap75: m('2.50', '39'),
    tap50: m('2.70', '36'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M3.5',
    pitch: '0.60',
    tap75: m('2.90', '32'),
    tap50: m('3.10', '31'),
    closeFit: m('3.70', '27'),
    freeFit: m('3.85', '24'),
  },
  {
    screwSize: '',
    pitch: '0.75',
    tap75: m('3.25', '30'),
    tap50: m('3.50', '28'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M4',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('4.20', '19'),
    freeFit: m('4.40', '17'),
  },
  {
    screwSize: '',
    pitch: '0.70',
    tap75: m('3.30', '30'),
    tap50: m('3.50', '28'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M4.5',
    pitch: '0.75',
    tap75: m('3.75', '25'),
    tap50: m('4.00', '22'),
    closeFit: m('4.75', '13'),
    freeFit: m('5.00', '9'),
  },
  {
    screwSize: '',
    pitch: '1.00',
    tap75: m('4.00', '21'),
    tap50: m('4.40', '11/64'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M5',
    pitch: '0.90',
    tap75: m('4.10', '20'),
    tap50: m('4.40', '17'),
    closeFit: m('5.25', '5'),
    freeFit: m('5.50', '0.22'),
  },
  {
    screwSize: '',
    pitch: '0.80',
    tap75: m('4.20', '19'),
    tap50: m('4.50', '16'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M5.5',
    pitch: '0.90',
    tap75: m('4.60', '14'),
    tap50: m('4.90', '10'),
    closeFit: m('5.80', '1'),
    freeFit: m('6.10', 'B'),
  },
  {
    screwSize: '',
    pitch: '1.00',
    tap75: m('5.00', '8'),
    tap50: m('5.40', '4'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M6',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('6.30', 'E'),
    freeFit: m('6.60', 'G'),
  },
  {
    screwSize: '',
    pitch: '0.75',
    tap75: m('5.25', '4'),
    tap50: m('5.50', '7/32'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    pitch: '1.00',
    tap75: m('6.00', 'B'),
    tap50: m('6.40', 'E'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M7',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('7.40', 'L'),
    freeFit: m('7.70', 'N'),
  },
  {
    screwSize: '',
    pitch: '0.75',
    tap75: m('6.25', 'D'),
    tap50: m('6.50', 'F'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    pitch: '1.25',
    tap75: m('6.80', 'H'),
    tap50: m('7.20', 'J'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M8',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('8.40', 'Q'),
    freeFit: m('8.80', 'S'),
  },
  {
    screwSize: '',
    pitch: '1.00',
    tap75: m('7.00', 'J'),
    tap50: m('7.40', 'L'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    pitch: '1.25',
    tap75: m('7.80', 'N'),
    tap50: m('8.20', 'P'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M9',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('9.50', '3/8'),
    freeFit: m('9.90', '25/64'),
  },
  {
    screwSize: '',
    pitch: '1.00',
    tap75: m('8.00', 'O'),
    tap50: m('8.40', '21/64'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    pitch: '1.50',
    tap75: m('8.50', 'R'),
    tap50: m('9.00', 'T'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M10',
    pitch: '1.25',
    tap75: m('8.80', '11/32'),
    tap50: m('9.20', '23/64'),
    closeFit: m('10.50', 'Z'),
    freeFit: m('11.00', '0.4375'),
  },
  {
    screwSize: '',
    pitch: '1.00',
    tap75: m('9.00', 'T'),
    tap50: m('9.40', 'U'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M11',
    pitch: '1.50',
    tap75: m('9.50', '3/8'),
    tap50: m('10.00', 'X'),
    closeFit: m('11.60', '29/64'),
    freeFit: m('12.10', '15/32'),
  },
  {
    screwSize: '',
    pitch: '1.75',
    tap75: m('10.30', '13/32'),
    tap50: m('10.90', '27/64'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M12',
    pitch: '1.50',
    tap75: m('10.50', 'Z'),
    tap50: m('11.00', '7/16'),
    closeFit: m('12.60', '1/2'),
    freeFit: m('13.20', '33/64'),
  },
  {
    screwSize: '',
    pitch: '1.25',
    tap75: m('10.80', '27/64'),
    tap50: m('11.20', '7/16'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    pitch: '2.00',
    tap75: m('12.10', '15/32'),
    tap50: m('12.70', '1/2'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M14',
    pitch: '1.50',
    tap75: m('12.50', '1/2'),
    tap50: m('13.00', '33/64'),
    closeFit: m('14.75', '37/64'),
    freeFit: m('15.50', '39/64'),
  },
  {
    screwSize: '',
    pitch: '1.25',
    tap75: m('12.80', '1/2'),
    tap50: m('13.20', '33/64'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M15',
    pitch: '1.50',
    tap75: m('13.50', '17/32'),
    tap50: m('14.00', '35/64'),
    closeFit: m('15.75', '5/8'),
    freeFit: m('16.50', '21/32'),
  },
  {
    screwSize: '',
    pitch: '2.00',
    tap75: m('14.00', '35/64'),
    tap50: m('14.75', '37/64'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M16',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('16.75', '21/32'),
    freeFit: m('17.50', '0.6875'),
  },
  {
    screwSize: '',
    pitch: '1.50',
    tap75: m('14.50', '37/64'),
    tap50: m('15.00', '19/32'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    pitch: '1.50',
    tap75: m('15.50', '39/64'),
    tap50: m('16.00', '5/8'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M17',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('18.00', '45/64'),
    freeFit: m('18.50', '47/64'),
  },
  {
    screwSize: '',
    pitch: '2.50',
    tap75: m('15.50', '39/64'),
    tap50: m('16.50', '41/64'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    pitch: '2.00',
    tap75: m('16.00', '5/8'),
    tap50: m('16.75', '21/32'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M18',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('19.00', '3/4'),
    freeFit: m('20.00', '25/32'),
  },
  {
    screwSize: '',
    pitch: '1.50',
    tap75: m('16.50', '21/32'),
    tap50: m('17.00', '43/64'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    pitch: '2.50',
    tap75: m('16.50', '21/32'),
    tap50: m('17.50', '11/16'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M19',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('20.00', '25/32'),
    freeFit: m('21.00', '53/64'),
  },
  {
    screwSize: '',
    pitch: '2.50',
    tap75: m('17.50', '11/16'),
    tap50: m('18.50', '23/32'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: '',
    pitch: '2.00',
    tap75: m('18.00', '45/64'),
    tap50: m('18.50', '47/64'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
  {
    screwSize: 'M20',
    pitch: '',
    tap75: EMPTY_DRILL,
    tap50: EMPTY_DRILL,
    closeFit: m('21.00', '53/64'),
    freeFit: m('22.00', '55/64'),
  },
  {
    screwSize: '',
    pitch: '1.50',
    tap75: m('18.50', '47/64'),
    tap50: m('19.00', '3/4'),
    closeFit: EMPTY_DRILL,
    freeFit: EMPTY_DRILL,
  },
];

export const STANDARD_TAP_ROWS = compactStandardRows(STANDARD_TAP_ROWS_SOURCE);
export const METRIC_TAP_ROWS = compactMetricRows(METRIC_TAP_ROWS_SOURCE);

export const PDF_SOURCE_URL =
  'https://web.mae.ufl.edu/designlab/Lab%20Assignments/EML2322L%20Tap%20Drill%20Chart.pdf';
